import { useState, useEffect, ReactNode } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { defaultLocale, loadMessages } from './config';
import { useLocale } from './useLocale';

interface IntlProviderProps {
  children: ReactNode;
}

// Flatten nested message object into dot-notation keys
function flattenMessages(obj: any, prefix = ''): Record<string, string> {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'object' && value !== null) {
        Object.assign(acc, flattenMessages(value, newKey));
      } else {
        acc[newKey] = value;
      }

      return acc;
    },
    {} as Record<string, string>,
  );
}

export function IntlProvider({ children }: IntlProviderProps) {
  const { locale } = useLocale();
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const loadedMessages = await loadMessages(locale);
        setMessages(flattenMessages(loadedMessages));
      } catch (error) {
        console.error('Failed to load messages:', error);
        // Fall back to default locale
        const defaultMessages = await loadMessages(defaultLocale);
        setMessages(flattenMessages(defaultMessages));
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [locale]);

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <ReactIntlProvider
      locale={locale}
      defaultLocale={defaultLocale}
      messages={messages}
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION') {
          console.warn('Missing translation:', err.message);
          return;
        }
        throw err;
      }}
    >
      {children}
    </ReactIntlProvider>
  );
}
